/* eslint-disable max-nested-callbacks */
import { get } from 'lodash';
import { useEffect, useState } from 'react';

import { CategoryRef, ItemWithProperties } from '~/mappers/itemListing/models';
import { gtmService } from '~/services/gtm';
import { SalsifyProductData } from '~/types/common';
import {
  generateLocationQueryString,
  getLocationQueryStringParam,
} from '~/utils/browser';
import { QUERY_STRING_PARAM_STRATEGY } from '~/utils/constants';

import { QUERY_STRING_PARAMS } from './constants';
import { SORT_BY, SortBy, UseFilterOptions } from './models';
import { getFilterValuesFromQueryString } from './utils';

export const useItemFilters = <
  T extends ItemWithProperties = ItemWithProperties
>(
  items: T[],
  { filters = [] }: UseFilterOptions
) => {
  const [filteredData, setFilteredData] = useState<T[]>(items);
  const [filter, setFilter] = useState<{ group: string; value: CategoryRef }[]>(
    getFilterValuesFromQueryString(
      filters,
      getLocationQueryStringParam(QUERY_STRING_PARAMS.FILTER) as string[] | []
    )
  );
  const [sort, setSort] = useState<SortBy>(
    (getLocationQueryStringParam(
      QUERY_STRING_PARAMS.SORT,
      QUERY_STRING_PARAM_STRATEGY.STRING
    ) as SortBy) || SORT_BY.DEFAULT
  );
  const [activeGroup, setActiveGroup] = useState('');

  const handleFilterChange = (group: string, value: CategoryRef) => {
    const { uid } = value;

    setActiveGroup(group);
    setFilter((prev) => {
      const isAlreadyIncluded = prev.find((item) => item.value.uid === uid);
      if (isAlreadyIncluded) {
        return prev.filter((item) => item.value.uid !== uid);
      }

      return [...prev, { group, value }];
    });
  };

  const handleSortChange = (sortBy: SortBy) => {
    setSort(sortBy);
  };

  const handleGroupReset = (name: string) => {
    setFilter((prev) => prev.filter((item) => item.group !== name));
  };

  const handleActiveGroup = (group: string) => {
    setActiveGroup((current) => (current === group ? '' : group));
  };

  const getActiveCountForGroup = (name: string) =>
    filter.filter((item) => item.group === name).length;

  useEffect(() => {
    const sortedItems = [...items];

    if (sort !== SORT_BY.DEFAULT) {
      sortedItems.sort((a, b) => {
        const nameA = get(a, 'name', '') as string;
        const nameB = get(b, 'name', '') as string;
        const createdAtA = get(a, 'updatedAt', '') as string;
        const createdAtB = get(b, 'updatedAt', '') as string;

        switch (sort) {
          case SORT_BY.NAME_ASC:
            return nameA.localeCompare(nameB);
          case SORT_BY.NAME_DESC:
            return nameB.localeCompare(nameA);
          case SORT_BY.RECENT:
            return (
              new Date(createdAtB).getTime() - new Date(createdAtA).getTime()
            );
          default:
            return 0;
        }
      });
    }

    const groups = [...new Set(filter.map((item) => item.group))]
      .map((name) => ({
        name,
        uids: filter
          .filter(({ group }) => group === name)
          .map((item) => item.value.uid),
      }))
      .filter(({ uids }) => uids.length > 0);

    const filteredItems = sortedItems.filter((item) => {
      const itemCategories: CategoryRef[] = get(item, 'categories', []);
      if (filter.length === 0 || itemCategories.length === 0) return true;

      return groups.every((group) =>
        group.uids.every((uid) =>
          itemCategories.some((category) => category.uid === uid)
        )
      );
    });

    const filteredItemsProductData = (
      filteredItems as {
        fields?: { productData: unknown };
        productData?: unknown;
      }[]
    )
      .map((props) =>
        get(props, 'fields.productData', get(props, 'productData'))
      )
      .filter((productData) => productData !== undefined);

    gtmService.emitViewItemList(
      filteredItemsProductData as SalsifyProductData[],
      {
        ...(filter.length && { item_list_filter: activeGroup }),
        item_list_sort: sort,
      }
    );

    setFilteredData(groups.length > 0 ? filteredItems : sortedItems);

    const getActiveFilterUids = filter.map(
      ({ value: { uid: filterUid } }) => filterUid
    );

    window.history.pushState(
      null,
      '',
      generateLocationQueryString({
        sort: sort !== SORT_BY.DEFAULT ? sort : null,
        filter: getActiveFilterUids,
      })
    );
  }, [filter, sort, activeGroup, items]);

  return {
    data: filteredData,
    filter,
    sort,
    handleFilterChange,
    handleSortChange,
    handleGroupReset,
    handleActiveGroup,
    getActiveCountForGroup,
    activeGroup,
  };
};
